import { defineStore } from 'pinia';
import { AssessmentService } from '@/services';
import { AssessmentSuccessResponse, StoreInterface } from '@/types';
import { AssessmentFilterInterface } from '@/services/AssessmentService';
import {
  AssessmentInterface,
  AssessmentPayload,
  AssessmentOptions,
  ApiErrorResponse,
} from '@/types/AssessmentManagement';

interface AssessmentStoreStateInterface extends StoreInterface {
  assessments: {
    loading: boolean;
    data: AssessmentInterface[];
    total: number;
  };
  options: {
    loading: boolean;
    data: AssessmentOptions['data'] | null;
  };
  currentAssessment: {
    loading: boolean;
    data: AssessmentInterface | null;
    id: number | null;
  };
}

export default defineStore('AssessmentStore', {
  state: (): AssessmentStoreStateInterface => ({
    assessments: {
      loading: false,
      data: [],
      total: 0,
    },
    options: {
      loading: false,
      data: null,
    },
    currentAssessment: {
      loading: false,
      data: null,
      id: null,
    },
    message: '',
    error: false,
    loading: false,
  }),

  getters: {
    errorTitle: () => 'Assessment Error',
  },

  actions: {
    load() {
      this.error = false;
      this.message = '';
      this.loading = true;
    },

    catchError(e: ApiErrorResponse) {
      this.error = true;
      this.message = e.message;
      this.loading = false;
      throw e;
    },

    loaded(msg = '') {
      if (!this.error) {
        this.message = msg;
        this.loading = false;
      }
    },

    async getAssessments(filters?: AssessmentFilterInterface) {
      this.assessments.loading = true;
      try {
        const response = await AssessmentService.getAssessments(filters);
        this.assessments.data = response.data.data;
        this.assessments.total = response.data.meta.total;
      } catch (error) {
        this.catchError(error as ApiErrorResponse);
      } finally {
        this.assessments.loading = false;
      }
    },

    async getOptions() {
      this.options.loading = true;
      try {
        const response = await AssessmentService.getOptions();
        this.options.data = response.data.data;
      } catch (error) {
        this.catchError(error as ApiErrorResponse);
      } finally {
        this.options.loading = false;
      }
    },

    async refreshCurrentAssessment() {
      if (this.currentAssessment.id) {
        await this.getAssessment(this.currentAssessment.id);
      }
    },

    async getAssessment(id: number) {
      if (this.currentAssessment.id === id && this.currentAssessment.data) {
        return { data: this.currentAssessment.data };
      }

      this.load();
      this.currentAssessment.loading = true;
      try {
        const response = await AssessmentService.getAssessment(id);
        this.currentAssessment.data = response.data.data;
        this.currentAssessment.id = id;
        this.loaded();
        return response.data;
      } catch (error) {
        this.catchError(error as ApiErrorResponse);
      } finally {
        this.currentAssessment.loading = false;
      }
    },

    async createAssessment(payload: AssessmentPayload) {
      this.load();
      try {
        const response = await AssessmentService.createAssessment(payload);
        const { success } = response.data as AssessmentSuccessResponse;
        this.loaded();
        return success;
      } catch (error) {
        this.catchError(error as ApiErrorResponse);
      }
    },

    async updateAssessment(id: number, payload: AssessmentPayload) {
      this.load();
      try {
        const response = await AssessmentService.updateAssessment(id, payload);

        if (id === this.currentAssessment.id) {
          const updatedAssessment = response.data.data;
          if (updatedAssessment) {
            this.currentAssessment.data = updatedAssessment;
          }
        }

        this.loaded();
        const { success } = response.data as AssessmentSuccessResponse;
        return success;
      } catch (error) {
        this.catchError(error as ApiErrorResponse);
      }
    },

    async deleteAssessment(id: number) {
      this.load();
      try {
        const response = await AssessmentService.deleteAssessment(id);
        if (id === this.currentAssessment.id) {
          this.clearCurrentAssessment();
        }
        this.loaded();
        const { success } = response.data as AssessmentSuccessResponse;
        return success;
      } catch (error) {
        this.catchError(error as ApiErrorResponse);
      }
    },

    clearCurrentAssessment() {
      this.currentAssessment.data = null;
      this.currentAssessment.id = null;
    },
  },
});
